// Submenú "Tesorería" del módulo Control Bancario.
//   Programación de pagos · Órdenes de pago · Cobros · Flujo de caja
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AvisoRenombre } from '@/components/ui/AvisoRenombre';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Ban, Banknote, CalendarClock, CheckCircle2, ClipboardCheck, FileCheck, HandCoins, Loader2, Pencil, Plus, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import {
  Campo, Cobro, CuentaMini, CxpFacturaPicker, CxpPendiente, ESTADO_OP_CLS, FORMA_COBRO, FORMA_PAGO, FlujoCaja, Modal, OrdenPago, OrdenPorAprobar, PagoProgramado,
  cb, cbRpc, fieldCls, hoyISO, label, money,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

function useCuentas() {
  return useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });
}
const invalidar = (qc: ReturnType<typeof useQueryClient>) =>
  ['cb-programados', 'cb-ordenes', 'cb-cobros', 'cb-flujo', 'cb-libro', 'cb-posicion'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));

// ================================================= PROGRAMACIÓN DE PAGOS =======
const progVacio = { fecha_vencimiento: hoyISO(), beneficiario: '', monto: 0, concepto: '', categoria: '', documento_ref: '', prioridad: 100 };

export function ProgramacionPagos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cuentas = [] } = useCuentas();
  const { data: rows = [], isFetching } = useQuery<PagoProgramado[]>({
    queryKey: ['cb-programados'],
    queryFn: () => cb.list<PagoProgramado>('pagos_programados', '?select=*&deleted_at=is.null&order=fecha_vencimiento'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof progVacio>(progVacio);
  const [abierto, setAbierto] = useState(false);
  const [pagar, setPagar] = useState<PagoProgramado | null>(null);
  const [pf, setPf] = useState({ fecha: hoyISO(), forma_pago: 'transferencia', cuenta_id: '' });
  const set = (p: Partial<typeof progVacio>) => setForm((f) => ({ ...f, ...p }));

  const abrir = (p?: PagoProgramado) => {
    if (p) { setEditId(p.id); setForm({ fecha_vencimiento: p.fecha_vencimiento, beneficiario: p.beneficiario, monto: p.monto, concepto: p.concepto ?? '', categoria: p.categoria ?? '', documento_ref: p.documento_ref ?? '', prioridad: p.prioridad }); }
    else { setEditId(null); setForm(progVacio); }
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { ...form, monto: Number(form.monto), concepto: form.concepto || null, categoria: form.categoria || null, documento_ref: form.documento_ref || null, prioridad: Number(form.prioridad) || 100 };
      return editId ? cb.update('pagos_programados', editId, body) : cb.create('pagos_programados', body);
    },
    onSuccess: () => { toast.success('Pago programado guardado'); setAbierto(false); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => cb.update('pagos_programados', id, { estado: 'anulado' }),
    onSuccess: () => { toast.success('Anulado'); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const doPagar = useMutation({
    mutationFn: () => cbRpc('cb_pagar_programado', { _programado_id: pagar!.id, _fecha: pf.fecha, _forma_pago: pf.forma_pago, _cuenta_id: pf.cuenta_id }),
    onSuccess: (r: any) => { toast.success(`Pagado (orden ${r.numero})`); setPagar(null); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Programación de pagos</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Programar</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Vencimientos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Vence</th><th className="py-2 pr-3">Beneficiario</th><th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin pagos programados</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} className={`border-b last:border-0 hover:bg-muted/40 ${p.estado === 'anulado' ? 'opacity-50 line-through' : ''}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{p.fecha_vencimiento}</td>
                  <td className="py-2 pr-3">{p.beneficiario}</td>
                  <td className="py-2 pr-3">{p.concepto ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(p.monto, p.moneda)}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${p.estado === 'programado' ? 'bg-pendiente-soft text-pendiente' : p.estado === 'pagado' ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>{p.estado}</span></td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {p.estado === 'programado' && <>
                      <Button size="sm" variant="outline" onClick={() => { setPf({ fecha: hoyISO(), forma_pago: 'transferencia', cuenta_id: p.cuenta_id ?? cuentas[0]?.id ?? '' }); setPagar(p); }}><Banknote className="w-4 h-4 mr-1" /> Pagar</Button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Anular" onClick={async () => { if (await dlg.confirmar('¿Anular el pago programado?', { peligro: true })) anular.mutate(p.id); }}><Ban className="w-4 h-4" /></button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar programación' : 'Programar pago'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha de vencimiento *"><input type="date" className={fieldCls} value={form.fecha_vencimiento} onChange={(e) => set({ fecha_vencimiento: e.target.value })} /></Campo>
              <Campo label="Monto *"><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto} onChange={(e) => set({ monto: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Beneficiario *"><input className={fieldCls} value={form.beneficiario} onChange={(e) => set({ beneficiario: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Concepto"><input className={fieldCls} value={form.concepto} onChange={(e) => set({ concepto: e.target.value })} /></Campo>
              <Campo label="Documento ref."><input className={fieldCls} value={form.documento_ref} onChange={(e) => set({ documento_ref: e.target.value })} placeholder="Factura CxP" /></Campo>
            </div>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.beneficiario.trim() || Number(form.monto) <= 0} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}

      {pagar && (
        <Modal title={`Pagar: ${pagar.beneficiario}`} onClose={() => setPagar(null)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{money(pagar.monto, pagar.moneda)} · genera una orden de pago y el débito bancario.</p>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={pf.fecha} onChange={(e) => setPf({ ...pf, fecha: e.target.value })} /></Campo>
              <Campo label="Forma de pago"><select className={fieldCls} value={pf.forma_pago} onChange={(e) => setPf({ ...pf, forma_pago: e.target.value })}>{FORMA_PAGO.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}</select></Campo>
            </div>
            <Campo label="Cuenta *"><select className={fieldCls} value={pf.cuenta_id} onChange={(e) => setPf({ ...pf, cuenta_id: e.target.value })}><option value="">— Selecciona —</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}</select></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setPagar(null)}>Cancelar</Button>
              <Button isLoading={doPagar.isPending} disabled={!pf.cuenta_id} onClick={() => doPagar.mutate()}>Confirmar pago</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ==================================================== ÓRDENES DE PAGO ==========
const opVacio = { beneficiario: '', monto: 0, cuenta_id: '', forma_pago: 'transferencia', fecha: hoyISO(), concepto: '', documento_ref: '', ticket_buy_id: '' };

export function OrdenesPago() {
  const { t } = useTranslation('bancos');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cuentas = [] } = useCuentas();
  const { data: rows = [], isFetching } = useQuery<OrdenPago[]>({
    queryKey: ['cb-ordenes'],
    queryFn: () => cb.list<OrdenPago>('ordenes_pago', '?select=*,cuenta:cuenta_id(alias,numero)&deleted_at=is.null&order=created_at.desc&limit=200'),
  });
  const [form, setForm] = useState<typeof opVacio>(opVacio);
  const [abierto, setAbierto] = useState(false);
  const [pagar, setPagar] = useState<OrdenPago | null>(null);
  const [pf, setPf] = useState({ fecha: hoyISO(), cuenta_id: '' });
  const set = (p: Partial<typeof opVacio>) => setForm((f) => ({ ...f, ...p }));

  const headersExp = [t('ordenes.col_n'), t('ordenes.col_numero'), t('ordenes.col_fecha'), t('ordenes.col_beneficiario'), t('ordenes.col_cuenta'), t('ordenes.col_monto'), t('ordenes.col_estado')];
  const filasExport: Celda[][] = [
    ...rows.map((o, i): Celda[] => [
      i + 1, o.numero, o.fecha, o.beneficiario, o.cuenta?.alias ?? o.cuenta?.numero ?? '', Number(o.monto), t(`ordenes.estado.${o.estado}`),
    ]),
    ['', '', '', '', t('ordenes.total'), rows.reduce((a, o) => a + Number(o.monto), 0), ''],
  ];

  const crear = useMutation({
    mutationFn: () => cbRpc('cb_orden_pago_crear', { _beneficiario: form.beneficiario, _monto: Number(form.monto), _cuenta_id: form.cuenta_id, _forma_pago: form.forma_pago, _fecha: form.fecha, _concepto: form.concepto || null, _documento_ref: form.documento_ref || null, _ticket_buy_id: form.ticket_buy_id || null }),
    onSuccess: (r: any) => { toast.success(t('ordenes.creada', { numero: r.numero })); setAbierto(false); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const aprobar = useMutation({
    mutationFn: (id: string) => cbRpc('cb_orden_pago_aprobar', { _orden_id: id }),
    onSuccess: () => { toast.success(t('ordenes.aprobada_ok')); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const doPagar = useMutation({
    mutationFn: () => cbRpc('cb_orden_pago_pagar', { _orden_id: pagar!.id, _fecha: pf.fecha, _cuenta_id: pf.cuenta_id || null }),
    onSuccess: () => { toast.success(t('ordenes.pagada_ok')); setPagar(null); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => cbRpc('cb_orden_pago_anular', { _orden_id: id, _motivo: null }),
    onSuccess: () => { toast.success(t('ordenes.anulada_ok')); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><FileCheck className="w-5 h-5" /> {t('ordenes.titulo')}</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`ordenes-pago-${fechaLocal()}`} titulo={t('ordenes.titulo')} headers={headersExp} filas={filasExport}
            colsDerecha={[0, 5]} subtitulo={t('ordenes.sub_export', { fecha: new Date().toLocaleString(LOCALE), n: rows.length })}
            fmtPDF={(v, c) => (c === 5 ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
            disabled={rows.length === 0} />
          <Button onClick={() => { setForm({ ...opVacio, cuenta_id: cuentas[0]?.id ?? '' }); setAbierto(true); }} disabled={cuentas.length === 0}><Plus className="w-4 h-4 mr-1" /> {t('ordenes.btn_nueva')}</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('ordenes.lista', { n: rows.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('ordenes.col_n')}</th><th className="py-2 pr-3">{t('ordenes.col_fecha')}</th><th className="py-2 pr-3">{t('ordenes.col_beneficiario')}</th><th className="py-2 pr-3">{t('ordenes.col_cuenta')}</th><th className="py-2 pr-3 text-right">{t('ordenes.col_monto')}</th><th className="py-2 pr-3">{t('ordenes.col_estado')}</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{t('ordenes.vacio')}</td></tr>}
              {rows.map((o) => (
                <tr key={o.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{o.numero}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{o.fecha}</td>
                  <td className="py-2 pr-3">{o.beneficiario}</td>
                  <td className="py-2 pr-3">{o.cuenta?.alias ?? o.cuenta?.numero}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(o.monto, o.moneda)}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_OP_CLS[o.estado]}`}>{t(`ordenes.estado.${o.estado}`)}</span></td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {o.estado === 'borrador' && <Button size="sm" variant="outline" onClick={() => aprobar.mutate(o.id)}><CheckCircle2 className="w-4 h-4 mr-1" /> {t('ordenes.btn_aprobar')}</Button>}
                    {o.estado === 'aprobada' && <Button size="sm" variant="outline" onClick={() => { setPf({ fecha: hoyISO(), cuenta_id: '' }); setPagar(o); }}><Banknote className="w-4 h-4 mr-1" /> {t('ordenes.btn_pagar')}</Button>}
                    {(o.estado === 'borrador' || o.estado === 'aprobada' || o.estado === 'pagada') && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title={t('ordenes.anular')} onClick={async () => { if (await dlg.confirmar(t('ordenes.confirmar_anular'), { detalle: t('ordenes.detalle_anular'), peligro: true })) anular.mutate(o.id); }}><Ban className="w-4 h-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={t('ordenes.modal_nueva')} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label={t('ordenes.f_factura')}>
              <CxpFacturaPicker value={form.ticket_buy_id || null} onPick={(f: CxpPendiente | null) => set(f
                ? { ticket_buy_id: f.ticket_id, beneficiario: f.proveedor ?? form.beneficiario, monto: f.saldo, documento_ref: f.serie ?? '' }
                : { ticket_buy_id: '' })} />
              {form.ticket_buy_id && <p className="text-xs text-estable">{t('ordenes.aviso_factura')}</p>}
            </Campo>
            <Campo label={t('ordenes.f_beneficiario')}><input className={fieldCls} value={form.beneficiario} onChange={(e) => set({ beneficiario: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('ordenes.f_monto')}><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto} onChange={(e) => set({ monto: Number(e.target.value) })} /></Campo>
              <Campo label={t('ordenes.f_fecha')}><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('ordenes.f_cuenta')}><select className={fieldCls} value={form.cuenta_id} onChange={(e) => set({ cuenta_id: e.target.value })}><option value="">{t('ordenes.selecciona')}</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}</select></Campo>
              <Campo label={t('ordenes.f_forma_pago')}><select className={fieldCls} value={form.forma_pago} onChange={(e) => set({ forma_pago: e.target.value })}>{FORMA_PAGO.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}</select></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('ordenes.f_concepto')}><input className={fieldCls} value={form.concepto} onChange={(e) => set({ concepto: e.target.value })} /></Campo>
              <Campo label={t('ordenes.f_doc_ref')}><input className={fieldCls} value={form.documento_ref} onChange={(e) => set({ documento_ref: e.target.value })} /></Campo>
            </div>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>{t('ordenes.cancelar')}</Button>
              <Button isLoading={crear.isPending} disabled={!form.beneficiario.trim() || !form.cuenta_id || Number(form.monto) <= 0} onClick={() => crear.mutate()}>{t('ordenes.btn_crear')}</Button>
            </div>
          </div>
        </Modal>
      )}

      {pagar && (
        <Modal title={t('ordenes.modal_pagar', { numero: pagar.numero })} onClose={() => setPagar(null)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{pagar.beneficiario} · {money(pagar.monto, pagar.moneda)}</p>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('ordenes.f_fecha')}><input type="date" className={fieldCls} value={pf.fecha} onChange={(e) => setPf({ ...pf, fecha: e.target.value })} /></Campo>
              <Campo label={t('ordenes.f_cuenta_opc')}><select className={fieldCls} value={pf.cuenta_id} onChange={(e) => setPf({ ...pf, cuenta_id: e.target.value })}><option value="">{t('ordenes.la_de_la_orden')}</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.alias ?? c.numero}</option>)}</select></Campo>
            </div>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setPagar(null)}>{t('ordenes.cancelar')}</Button>
              <Button isLoading={doPagar.isPending} onClick={() => doPagar.mutate()}>{t('ordenes.btn_confirmar_pago')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ================================================= APROBACIÓN DE PAGOS =========
// Bandeja del aprobador: solo OP en borrador creadas por OTROS usuarios
// (segregación de funciones — quien crea no aprueba).
export function AprobacionPagos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<OrdenPorAprobar[]>({
    queryKey: ['cb-por-aprobar'],
    queryFn: () => cbRpc<OrdenPorAprobar[]>('cb_ordenes_por_aprobar'),
  });
  const inval = () => { void qc.invalidateQueries({ queryKey: ['cb-por-aprobar'] }); invalidar(qc); };
  const aprobar = useMutation({
    mutationFn: (id: string) => cbRpc('cb_orden_pago_aprobar', { _orden_id: id }),
    onSuccess: () => { toast.success('Orden aprobada'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rechazar = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => cbRpc('cb_orden_pago_rechazar', { _orden_id: id, _motivo: motivo || null }),
    onSuccess: () => { toast.success('Orden rechazada'); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><ClipboardCheck className="w-5 h-5" /> Bandeja de aprobación</h1>
      <p className="text-sm text-muted-foreground">
        Órdenes de pago pendientes de aprobar. Por control interno, <b>solo puedes aprobar las que creó otro usuario</b>
        (las tuyas no aparecen aquí). Al aprobar, quedan listas para pagarse en Órdenes de pago.
      </p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Pendientes de aprobar ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Beneficiario</th><th className="py-2 pr-3">Factura</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Creada por</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No hay órdenes pendientes de tu aprobación</td></tr>}
              {rows.map((o) => (
                <tr key={o.orden_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{o.numero}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{o.fecha}</td>
                  <td className="py-2 pr-3">{o.beneficiario}{o.concepto && <span className="block text-xs text-muted-foreground">{o.concepto}</span>}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{o.serie_factura ?? o.documento_ref ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(o.monto, o.moneda)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{o.creado_por ?? '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" isLoading={aprobar.isPending} onClick={() => aprobar.mutate(o.orden_id)}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                    </Button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Rechazar"
                      onClick={async () => {
                        const v = await dlg.pedir('Rechazar orden', [{ key: 'motivo', label: 'Motivo del rechazo (opcional)', minimo: 0 }]);
                        if (!v) return;
                        rechazar.mutate({ id: o.orden_id, motivo: v.motivo });
                      }}>
                      <Ban className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================ COBROS ===========
const cobroVacio = { fecha: hoyISO(), cliente: '', monto: 0, cuenta_id: '', forma_pago: 'transferencia', concepto: '', documento_ref: '' };

export function Cobros() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cuentas = [] } = useCuentas();
  const { data: rows = [], isFetching } = useQuery<Cobro[]>({
    queryKey: ['cb-cobros'],
    queryFn: () => cb.list<Cobro>('cobros', '?select=*,cuenta:cuenta_id(alias,numero)&deleted_at=is.null&order=fecha.desc,created_at.desc&limit=200'),
  });
  const [form, setForm] = useState<typeof cobroVacio>(cobroVacio);
  const [abierto, setAbierto] = useState(false);
  const set = (p: Partial<typeof cobroVacio>) => setForm((f) => ({ ...f, ...p }));

  const registrar = useMutation({
    mutationFn: () => cbRpc('cb_cobro_registrar', { _fecha: form.fecha, _cliente: form.cliente || null, _monto: Number(form.monto), _cuenta_id: form.cuenta_id, _forma_pago: form.forma_pago, _concepto: form.concepto || null, _documento_ref: form.documento_ref || null }),
    onSuccess: () => { toast.success('Cobro registrado'); setAbierto(false); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => cbRpc('cb_cobro_anular', { _cobro_id: id, _motivo: null }),
    onSuccess: () => { toast.success('Cobro anulado'); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <AvisoRenombre clave="cb-cobros" antes="Recaudaciones / cobros" />
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><HandCoins className="w-5 h-5" /> Registrar cobros</h1>
        <Button onClick={() => { setForm({ ...cobroVacio, cuenta_id: cuentas[0]?.id ?? '' }); setAbierto(true); }} disabled={cuentas.length === 0}><Plus className="w-4 h-4 mr-1" /> Registrar cobro</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cobros ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Cliente</th><th className="py-2 pr-3">Forma</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin cobros</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className={`border-b last:border-0 hover:bg-muted/40 ${c.estado === 'anulado' ? 'opacity-50 line-through' : ''}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{c.fecha}</td>
                  <td className="py-2 pr-3">{c.cliente ?? '—'}</td>
                  <td className="py-2 pr-3">{label(FORMA_COBRO, c.forma_pago)}</td>
                  <td className="py-2 pr-3">{c.cuenta?.alias ?? c.cuenta?.numero}</td>
                  <td className="py-2 pr-3">{c.concepto ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-estable">{money(c.monto, c.moneda)}</td>
                  <td className="py-2 pr-3">{c.estado}</td>
                  <td className="py-2 text-right">
                    {c.estado === 'registrado' && <button className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={async () => { if (await dlg.confirmar('¿Anular el cobro?', { detalle: 'Se reversa el crédito.', peligro: true })) anular.mutate(c.id); }}>Anular</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Registrar cobro" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
              <Campo label="Monto *"><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto} onChange={(e) => set({ monto: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Cliente / origen"><input className={fieldCls} value={form.cliente} onChange={(e) => set({ cliente: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Cuenta destino *"><select className={fieldCls} value={form.cuenta_id} onChange={(e) => set({ cuenta_id: e.target.value })}><option value="">— Selecciona —</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}</select></Campo>
              <Campo label="Forma de cobro"><select className={fieldCls} value={form.forma_pago} onChange={(e) => set({ forma_pago: e.target.value })}>{FORMA_COBRO.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}</select></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Concepto"><input className={fieldCls} value={form.concepto} onChange={(e) => set({ concepto: e.target.value })} /></Campo>
              <Campo label="Documento ref."><input className={fieldCls} value={form.documento_ref} onChange={(e) => set({ documento_ref: e.target.value })} placeholder="Factura CxC" /></Campo>
            </div>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={registrar.isPending} disabled={!form.cuenta_id || Number(form.monto) <= 0} onClick={() => registrar.mutate()}>Registrar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ========================================================= FLUJO DE CAJA =======
export function FlujoDeCaja() {
  const [dias, setDias] = useState(30);
  const { data, isFetching } = useQuery<FlujoCaja>({
    queryKey: ['cb-flujo', dias],
    queryFn: () => cbRpc<FlujoCaja>('cb_flujo_caja', { _dias: dias }),
  });
  const alerta = data && data.saldo_minimo < 0;

  return (
    <div className="p-6 space-y-4">
      <AvisoRenombre clave="cb-flujo-caja" antes="Flujo de caja" />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Proyección de flujo de caja</h1>
        <select className={`${fieldCls} w-auto`} value={dias} onChange={(e) => setDias(Number(e.target.value))}>
          {[7, 15, 30, 60, 90].map((d) => <option key={d} value={d}>Próximos {d} días</option>)}
        </select>
      </div>
      <p className="text-sm text-muted-foreground">Saldo disponible hoy proyectado contra los compromisos de egreso (pagos programados + cheques posfechados) del horizonte.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Saldo actual" valor={money(data?.saldo_actual)} fuerte />
        <Kpi label="Egresos comprometidos" valor={money(data?.total_egresos)} clase="text-pendiente" />
        <Kpi label="Saldo proyectado" valor={money(data?.saldo_proyectado)} fuerte clase={data && data.saldo_proyectado < 0 ? 'text-pendiente' : ''} />
        <Kpi label="Saldo mínimo del período" valor={money(data?.saldo_minimo)} fuerte clase={alerta ? 'text-pendiente' : 'text-estable'} />
      </div>
      {alerta && <div className="flex items-center gap-2 rounded-md bg-pendiente-soft text-pendiente px-3 py-2 text-sm"><AlertTriangle className="w-4 h-4" /> El saldo proyectado cae por debajo de cero en el período: revisa la programación de pagos.</div>}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Proyección ({data?.items.length ?? 0} compromisos)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Origen</th><th className="py-2 pr-3">Detalle</th><th className="py-2 pr-3 text-right">Egreso</th><th className="py-2 pr-3 text-right">Saldo proyectado</th>
            </tr></thead>
            <tbody>
              {(data?.items.length ?? 0) === 0 && !isFetching && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin compromisos en el horizonte</td></tr>}
              {(data?.items ?? []).map((it, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{it.fecha}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">{it.origen}</span></td>
                  <td className="py-2 pr-3">{it.detalle ?? '—'} {it.concepto && <span className="text-muted-foreground">· {it.concepto}</span>}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-pendiente">{money(it.monto)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums font-semibold ${it.saldo_proyectado < 0 ? 'text-pendiente' : ''}`}>{money(it.saldo_proyectado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
function Kpi({ label: l, valor, clase, fuerte }: { label: string; valor: string; clase?: string; fuerte?: boolean }) {
  return (
    <Card><CardContent className="py-3">
      <div className="text-xs text-muted-foreground">{l}</div>
      <div className={`font-mono tabular-nums ${fuerte ? 'text-lg font-semibold' : 'text-base'} ${clase ?? ''}`}>{valor}</div>
    </CardContent></Card>
  );
}
